import ApiService from '../../../../../services/apiService';
import { mapState } from 'vuex';
import { isNullOrEmpty } from '../../../../../utils/vuex';
import debounce from 'lodash/debounce';
import sortBy from 'lodash/sortBy';
import get from 'lodash/get';
import cloneDeep from 'lodash/cloneDeep';
export default {
  name: 'CoreDtwinStateVirtualDeviceEdit',
  props: {
    // the current edited binding
    lclModel: {
      type: Object,
      required: false,
      default: () => ({})
    },
    tenantId: {
      type: Number,
      required: true
    },
    // is this an 'edit', 'create', or mass-variable- 'import'?
    mode: {
      type: String,
      required: false,
      default: () => 'edit'
    },
    dataSources: {
      type: Array,
      required: true
    }
  },
  data: function () {
    return {
      loading: false,
      // the current result-set for the autocomplete device search
      virtualDevices: [],
      loadingDevices: false,
      virtualDeviceSearch: '',
      selectedVirtualDevice: null,
      // the key to find the found array of devices in `virtualDeviceFetch`
      virtualDeviceFetchVariable: 'virtualDevices',
      virtualDeviceVariableHeaders: [{
        sortable: true,
        text: this.$i18n_t('DigitalTwin.DigitalTwin.states.key'),
        value: 'key',
        align: 'left'
      }, {
        sortable: true,
        text: this.$i18n_t('DigitalTwin.DigitalTwin.states.value'),
        value: 'value.value',
        align: 'left'
      }, {
        sortable: true,
        text: this.$i18n_t('DigitalTwin.DigitalTwin.states.lastUpdate'),
        value: 'milliseconds_ago',
        align: 'left'
      }, {
        sortable: true,
        text: this.$i18n_t('DigitalTwin.DigitalTwin.states.unit'),
        value: 'properties.unit',
        align: 'left'
      }],
      virtualDeviceVariableFooterProps: {
        itemsPerPageText: this.$i18n_t('Common.rowsPerPage'),
        itemsPerPageOptions: [20, 50, {
          text: this.$i18n_t('Common.all'),
          value: -1
        }]
      },
      // the retrieved states for the selected device
      virtualDeviceStates: null,
      selectedVirtualDeviceState: []
    };
  },
  computed: {
    ...mapState('app', ['inpOutlined', 'inpFilled', 'image', 'color', 'UiTheme', 'permissions']),
    hasManageAllPermissions() {
      return this.permissions['all.manage'];
    },
    formIsValid() {
      return !isNullOrEmpty(this.selectedVirtualDevice) && !isNullOrEmpty(this.selectedVirtualDeviceState) && this.selectedVirtualDeviceState.length > 0;
    },
    requiredRule() {
      return [v => !!v || this.$i18n_t('Common.fieldRequired')];
    },
    virtualDeviceSourceName() {
      return (this.dataSources.find(ds => ds.type === 'virtual-device') || {}).name || this.$i18n_t('DigitalTwin.VirtualDevice.title');
    }
  },
  watch: {
    virtualDeviceSearch(val) {
      if (!val || val.length < 1) {
        // if search value is empty or less than two characters do not start a search
        // but treat the old search as invalid as a new search is as about to happen
        // in this state no results will be shown in the auto-complete dropdown
        // because we set `virtualDevices` to an array with only `selectedVirtualDevice`
        // which will not show up because auto-completes `hide-selected` is true
        // while `loadingDevices` is true or the search is empty or less than two characters
        this.virtualDevices = [this.selectedVirtualDevice].filter(d => d);
        return;
      }

      // do not trigger the device-search (again) after the item has been selected
      // that would clear the variable-set
      if (!this.selectedVirtualDevice || !this.selectedVirtualDevice.title || val !== this.selectedVirtualDevice.title) {
        // set loadingDevices to true as early as the debounced fetchDevices will be called
        // to not delay the loading state when settings it from the debounced method
        this.loadingDevices = true;
        this.fetchDevices();
      }
    },
    selectedVirtualDevice(val) {
      if (val !== null) {
        this.fetchStates();
      }
      this.emitSelection();
    },
    selectedVirtualDeviceState() {
      this.emitSelection();
    }
  },
  created() {
    if (this.mode !== 'create' && this.mode !== 'import' && get(this.lclModel, 'properties.binding.options.virtualDevice.id')) {
      ApiService.virtualDeviceGet(this, get(this.lclModel, 'properties.binding.options.virtualDevice.id'), true).then(res => {
        let device = res;
        if (!device || !device.id) {
          throw new Error('NIOTA_DEVICE_NOT_FOUND');
        }
        this.virtualDevices = device ? [device] : null;
        this.selectedVirtualDevice = device;
      }).catch(error => {
        console.error(get(error, 'response.status'));
        if (get(error, 'response.status') === 403) {
          // user has no permission to read the device
          // probably some other user linked it, and this user cannot read it
          // so emit the no-permission event here to trigger the edit-dialog to be closed
          this.$emit('no-permission', this.$i18n_t('DigitalTwin.DigitalTwin.states.noPermissionError'));
          return;
        }
        console.error(error);
        let errorMessage = get(error, 'response.data.message', error.message);
        let errorOutput = this.$i18n_te(`Error.${errorMessage}`) ? this.$i18n_t(`Error.${errorMessage}`) : this.$i18n_t(errorMessage);
        this.$snack(errorOutput, 'error');
        this.virtualDevices = null;
        this.selectedVirtualDevice = null;
      }).finally(() => {
        this.loading = false;
      });
      this.selectedVirtualDeviceState = [{
        id: get(this.lclModel, 'properties.binding.options.virtualDevice.stateId')
      }];
    }
  },
  mounted() {
    this.lclModel.properties.binding.options = {
      ...this.lclModel.properties.binding.options,
      virtualDevice: {
        ...this.lclModel.properties.binding.options.virtualDevice
      }
    };
  },
  methods: {
    get,
    emitSelection() {
      this.$emit('bridge-binding-changed', {
        identifier: get(this.selectedVirtualDevice, 'id'),
        valueType: get(this.selectedVirtualDeviceState, '[0].properties.valueType'),
        variable: get(this.selectedVirtualDeviceState, '[0].key'),
        value: get(this.selectedVirtualDeviceState, '[0].value.value')
      });
    },
    // put the request in separate function, in case some components needs to overwrite it with own logic
    // e.g. in the heat-monitoring module where a custom list of "meters" with custom search-filter needs to be requested
    fetchRequest() {
      return ApiService.virtualDeviceFetch(this, null, null, null, ['bare'], null, 0, 50, [{
        or: [{
          variable: 'title',
          search: this.virtualDeviceSearch
        }, {
          variable: 'id',
          search: this.virtualDeviceSearch
        }, {
          variable: 'deviceId',
          search: this.virtualDeviceSearch
        }]
      }]);
    },
    fetchDevices: debounce(function () {
      this.loadingDevices = true;
      this.fetchRequest().then(res => {
        this.virtualDevices = get(res, this.virtualDeviceFetchVariable) || [];
      }).catch(error => {
        console.error(error);
        let errorMessage = get(error, 'response.data.message', error.message);
        let errorOutput = this.$i18n_te(`Error.${errorMessage}`) ? this.$i18n_t(`Error.${errorMessage}`) : this.$i18n_t(errorMessage);
        this.$snack(errorOutput, 'error');
        // logException(error);
        this.virtualDevices = [];
      }).finally(() => {
        this.loadingDevices = false;
      });
    }, 250),
    fetchStates() {
      this.virtualDeviceStates = null;
      if (!get(this, 'selectedVirtualDevice.id')) {
        return;
      }
      ApiService.digitalTwinFetchStates(this, this.selectedVirtualDevice.id).then(states => {
        this.virtualDeviceStates = states.map(state => {
          if (state.value.time) {
            state['milliseconds_ago'] = new Date() - new Date(state.value.time);
            state['time_ago'] = state['milliseconds_ago'];
            const units = ['seconds', 'minutes', 'hours', 'days', 'months'];
            const conversion = [1000, 60, 60, 24, 30];
            let selectedUnit = 0;
            while (state['time_ago'] >= conversion[selectedUnit] && selectedUnit < conversion.length) {
              state['time_ago'] = Math.floor(state['time_ago'] / conversion[selectedUnit]);
              selectedUnit++;
            }
            selectedUnit--;
            if (selectedUnit >= 0) {
              state['time_ago'] = `${state['time_ago']} ${units[selectedUnit]}`;
            }
          }
          return state;
        });
        this.virtualDeviceStates = sortBy(this.virtualDeviceStates, 'properties.orderIdx', 'createdAt');
        this.selectedVirtualDeviceState = cloneDeep(this.selectedVirtualDeviceState);
      }).catch(error => {
        console.error(error);
        let errorMessage = get(error, 'response.data.message', error.message);
        let errorOutput = this.$i18n_te(`Error.${errorMessage}`) ? this.$i18n_t(`Error.${errorMessage}`) : this.$i18n_t(errorMessage);
        this.$snack(errorOutput, 'error');
        // logException(error);

        this.virtualDeviceStates = [];
      });
    },
    saveState() {
      this.lclModel.properties.binding.options.configId = undefined;
      this.lclModel.properties.binding.options.virtualDevice = {
        id: get(this.selectedVirtualDevice, 'id'),
        stateId: get(this.selectedVirtualDeviceState, '[0].id')
      };
    }
  }
};